import type { RQLASTNode, RQLFieldDefinition } from "./types";
import { RQLEvaluatorError } from "./types";

export function evaluate<T>(
  ast: RQLASTNode,
  data: T[],
  fieldDefinitions: Record<string, RQLFieldDefinition>,
): T[] {
  try {
    return data.filter((item) => evaluateNode(ast, item, fieldDefinitions));
  } catch (error) {
    if (error instanceof RQLEvaluatorError) {
      throw error;
    }
    throw new RQLEvaluatorError(
      `Evaluation error: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

function evaluateNode<T>(
  node: RQLASTNode,
  item: T,
  fieldDefinitions: Record<string, RQLFieldDefinition>,
): boolean {
  switch (node.type) {
    case "comparison":
      return evaluateComparison(node, item, fieldDefinitions);

    case "logical": {
      const leftResult = evaluateNode(node.left, item, fieldDefinitions);
      const rightResult = evaluateNode(node.right, item, fieldDefinitions);

      if (node.operator === "and") {
        return leftResult && rightResult;
      } else if (node.operator === "or") {
        return leftResult || rightResult;
      } else {
        throw new RQLEvaluatorError(
          `Unknown logical operator: ${node.operator}`,
        );
      }
    }

    case "group":
      return evaluateNode(node.expression, item, fieldDefinitions);

    default:
      throw new RQLEvaluatorError(
        `Unknown AST node type: ${(node as any).type}`,
      );
  }
}

function evaluateComparison<T>(
  node: Extract<RQLASTNode, { type: "comparison" }>,
  item: T,
  fieldDefinitions: Record<string, RQLFieldDefinition>,
): boolean {
  const fieldDef = fieldDefinitions[node.field];
  if (!fieldDef) {
    throw new RQLEvaluatorError(`Unknown field: ${node.field}`);
  }

  let itemValue: any;
  try {
    itemValue = fieldDef.accessor(item);
  } catch (error) {
    throw new RQLEvaluatorError(
      `Error accessing field '${node.field}': ${error instanceof Error ? error.message : String(error)}`,
    );
  }

  const queryValue = node.value;

  if (fieldDef.type === "number") {
    const numItemValue =
      typeof itemValue === "number" ? itemValue : parseFloat(String(itemValue));
    const numQueryValue =
      typeof queryValue === "number"
        ? queryValue
        : parseFloat(String(queryValue));

    if (isNaN(numItemValue) || isNaN(numQueryValue)) {
      return false;
    }

    return compareNumbers(numItemValue, node.operator, numQueryValue);
  }

  if (fieldDef.type === "string") {
    const strItemValue = String(itemValue ?? "");
    const strQueryValue = String(queryValue);

    return compareStrings(strItemValue, node.operator, strQueryValue);
  }

  if (fieldDef.type === "boolean") {
    const boolItemValue = Boolean(itemValue);
    const boolQueryValue = Boolean(queryValue);

    if (node.operator === "=") {
      return boolItemValue === boolQueryValue;
    } else if (node.operator === "!=") {
      return boolItemValue !== boolQueryValue;
    } else {
      throw new RQLEvaluatorError(
        `Boolean fields only support '=' and '!=' operators, got: ${node.operator}`,
      );
    }
  }

  throw new RQLEvaluatorError(`Unsupported field type: ${fieldDef.type}`);
}

function compareNumbers(
  itemValue: number,
  operator: string,
  queryValue: number,
): boolean {
  switch (operator) {
    case "=":
      return itemValue === queryValue;
    case "!=":
      return itemValue !== queryValue;
    case ">":
      return itemValue > queryValue;
    case ">=":
      return itemValue >= queryValue;
    case "<":
      return itemValue < queryValue;
    case "<=":
      return itemValue <= queryValue;
    default:
      throw new RQLEvaluatorError(`Unknown comparison operator: ${operator}`);
  }
}

function compareStrings(
  itemValue: string,
  operator: string,
  queryValue: string,
): boolean {
  switch (operator) {
    case "=":
      return itemValue.toLowerCase().includes(queryValue.toLowerCase());
    case "!=":
      return !itemValue.toLowerCase().includes(queryValue.toLowerCase());
    case ">":
      return itemValue.localeCompare(queryValue) > 0;
    case ">=":
      return itemValue.localeCompare(queryValue) >= 0;
    case "<":
      return itemValue.localeCompare(queryValue) < 0;
    case "<=":
      return itemValue.localeCompare(queryValue) <= 0;
    default:
      throw new RQLEvaluatorError(`Unknown comparison operator: ${operator}`);
  }
}
